import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    translateWarning,
    type TranslatableWarning as CommonTranslatableWarning,
    type TranslatedWarning as CommonTranslatedWarning,
} from "verdi-common/i18n";

import { NAMESPACES } from "@/i18n/constants";

export type TranslatableWarning = CommonTranslatableWarning;
export type TranslatedWarning = CommonTranslatedWarning;
export type TranslatedWarningProblem = TranslatedWarning["problems"][number];
export type TranslatedWarningFix = TranslatedWarning["fixes"][number];

/**
 * Resolve a warning's localized `title`, `description`, `problems`, and `fixes` from the
 * `warnings` i18next catalog. Stored `warningTitle` / `warningDetails` are used as
 * `defaultValue` so any uncatalogued type or locale still renders, and
 * `warningParams` / `problemParams` / `fixParams` are passed under `replace` (with
 * reserved-name entries stripped) so a key like `count` can't hijack i18next pluralization.
 * Returns empty strings / empty lists when no warning is provided.
 */
export function useTranslatedWarning(warning: TranslatableWarning | null | undefined): TranslatedWarning {
    const { t: tWarnings } = useTranslation(NAMESPACES.WARNINGS);

    // Memoized so a stable warning object doesn't re-run catalog lookups and list
    // translation on every re-render of the card tree.
    return useMemo(() => {
        if (!warning) {
            return { title: "", description: "", problems: [], fixes: [] };
        }
        return translateWarning(warning, tWarnings);
    }, [warning, tWarnings]);
}
